import React from 'react';

import { Grid, RHFSelect, RHFTextInput } from '@cowbell/ui';

import type { Languages } from '../../../../i18n/i18n.language-config';
import { Show } from '../../../Show';
import { addressLabelsFor, stateOptionsFor } from './org-address-labels';
import { FIELD_SPAN, QUARTER_SPAN } from './org-form-layout';

interface OrgAddressFieldsProps {
  readonly language: Languages;
  readonly layout: Record<string, boolean>;
  /** `mailingAddress.` for the mailing panel; empty for the business address. */
  readonly fieldPrefix?: string;
  readonly disabledFields?: readonly string[];
}

/**
 * The address fields, in @cowbell/ui components.
 *
 * <p>Which fields appear and what they are called still comes from the
 * platform's country -- the UK gets a third address line, a Town/City and a
 * Postcode and no state; Australia gets a State/Territory. The supplied design
 * draws a US-only form, so following it literally would have broken those
 * platforms.
 */
export const OrgAddressFields = ({
  language,
  layout,
  fieldPrefix = '',
  disabledFields = [],
}: OrgAddressFieldsProps) => {
  const labels = addressLabelsFor(language);
  const states = stateOptionsFor(language);
  const field = (name: string) => `${fieldPrefix}${name}`;
  const isDisabled = (name: string) => disabledFields.includes(field(name));

  return (
    <>
      <Show when={Boolean(layout.showAddress1)}>
        <Grid.Col span={FIELD_SPAN}>
          <RHFTextInput
            name={field('address1')}
            label={labels.address1}
            withAsterisk
            disabled={isDisabled('address1')}
          />
        </Grid.Col>
      </Show>

      <Show when={Boolean(layout.showAddress2)}>
        <Grid.Col span={FIELD_SPAN}>
          <RHFTextInput
            name={field('address2')}
            label={labels.address2}
            placeholder="Suite, floor, unit (optional)"
            disabled={isDisabled('address2')}
          />
        </Grid.Col>
      </Show>

      <Show when={Boolean(layout.showAddress3)}>
        <Grid.Col span={FIELD_SPAN}>
          <RHFTextInput
            name={field('address3')}
            label={labels.address3}
            disabled={isDisabled('address3')}
          />
        </Grid.Col>
      </Show>

      <Show when={Boolean(layout.showCity)}>
        <Grid.Col span={FIELD_SPAN}>
          <RHFTextInput
            name={field('city')}
            label={labels.city}
            withAsterisk
            disabled={isDisabled('city')}
          />
        </Grid.Col>
      </Show>

      <Show when={Boolean(layout.showState)}>
        <Grid.Col span={QUARTER_SPAN}>
          <RHFSelect
            name={field('state')}
            label={labels.state}
            withAsterisk
            searchable
            data={states as { value: string; label: string }[]}
            disabled={isDisabled('state')}
          />
        </Grid.Col>
      </Show>

      <Show when={Boolean(layout.showPostCode)}>
        <Grid.Col span={QUARTER_SPAN}>
          <RHFTextInput
            name={field('zipCode')}
            label={labels.postalCode}
            withAsterisk
            disabled={isDisabled('zipCode')}
          />
        </Grid.Col>
      </Show>
    </>
  );
};

export default OrgAddressFields;
